import { styled } from './styled';
import parseColor from './util/parseColor';

export const styles = ({
  theme,
  color = 'primary',
  offColor = 'grey.400',
  size = 'medium',
}) => {
  const padding = (() => {
    const sizes = {
      small: {
        input: theme.spacing(1.25, 2),
        inputSide: theme.spacing(2),
        select: theme.spacing(1.25, 4, 1.25, 2),
      },
      medium: {
        input: theme.spacing(2.0625, 1.75),
        inputSide: theme.spacing(1.75),
        select: theme.spacing(2.0625, 3.75, 2.0625, 1.75),
      },
    };
    return sizes[size] || sizes.medium;
  })();
  const font = (() => {
    const sizes = {
      small: {
        input: {
          fontSize: '14px',
          letterSpacing: '0.15px',
          lineHeight: '1.4375em',
        },
        label: {
          grown: {
            fontSize: '14px',
            letterSpacing: '0.15px',
            transform: `translate(${theme.spacing(2)}, ${theme.spacing(1.25)})`,
          },
        },
      },
      medium: {
        input: {
          fontSize: '16px',
          letterSpacing: '0.15px',
          lineHeight: '1.4375em',
        },
        label: {
          grown: {
            fontSize: '16px',
            letterSpacing: '0.15px',
            transform: `translate(${theme.spacing(1.75)}, ${theme.spacing(2.0625)})`,
          },
        },
      },
    };
    return sizes[size] || sizes.medium;
  })();
  return {
    '& .MuiInputBase-root.MuiOutlinedInput-root': {
      borderRadius: '4px',
      // These inputs come with extra padding, remove it
      '&.MuiInputBase-multiline, &.MuiAutocomplete-inputRoot': {
        padding: 0,
      },
      // These inputs need extra padding, add it
      '&.MuiInputBase-adornedStart': {
        paddingLeft: padding.inputSide,
      },
      '&.MuiInputBase-adornedEnd': {
        paddingRight: padding.inputSide,
      },
      // When focused and not errored, color the notch
      '&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
        borderColor: parseColor(theme, color, 'light'),
      },
      // Have to shift this when the border is thicker
      '&.Mui-focused .MuiOutlinedInput-notchedOutline legend span': {
        marginLeft: '-1px',
      },
      // Text-based input styles
      '& .MuiInputBase-input, .MuiAutocomplete-input, .MuiSelect-select, .MuiNativeSelect-select': {
        ...font.input,
        height: font.input.lineHeight,
        padding: padding.input,
        '&.MuiSelect-select, &.MuiNativeSelect-select': {
          padding: padding.select,
        },
        '&.MuiInputBase-inputAdornedStart': {
          paddingLeft: 0,
        },
        '&.MuiInputBase-inputAdornedEnd': {
          paddingRight: 0,
        },
      },
    },
    '& .MuiInputLabel-root': {
      '&.MuiInputLabel-animated:not(.MuiInputLabel-shrink)': {
        ...font.label.grown,
        // add extra padding when select adornment is present
        '&:has(+ .MuiInputBase-root > .MuiSelect-select)': {
          paddingRight: '23px',
        },
      },
      '&.MuiInputLabel-shrink': {
        fontFamily: 'Inter,"Helvetica Neue",Helvetica,Arial,sans-serif', // No BlinkMacSystemFont, breaks
        fontSize: '16px',
        letterSpacing: '0.5px',
        transform: 'translate(14px, -9px) scale(0.75)',
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
        '&:not(.Mui-error)': {
          color: parseColor(theme, color, 'light'),
        },
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      top: '-8px',
      '& legend ': {
        fontFamily: 'Inter,"Helvetica Neue",Helvetica,Arial,sans-serif', // No BlinkMacSystemFont, breaks
        fontSize: 'calc(0.75 * 16px)',
        letterSpacing: 'calc(0.75 * 0.5px)',
        height: '1.4375em',
        lineHeight: '1.4375em',
        '& span': {
          fontFamily: 'inherit',
          fontSize: 'inherit',
          letterSpacing: 'inherit',
          height: 'inherit',
          lineHeight: 'inherit',
          transform: 'translateY(-1px)',
        },
      },
    },
    '&.MuiFormControlLabel-root .MuiTypography-root.MuiFormControlLabel-label': {
      ...font.input,
    },
    '&.MuiCheckbox-root': {
      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
      '&:not(.Mui-disabled)': {
        '&.Mui-checked': {
          color: parseColor(theme, color, 'light'),
        },
        '&:not(.Mui-checked)': {
          color: parseColor(theme, offColor),
        },
      },
    },
    // Checkbox styles
    '&.MuiFormControlLabel-root .MuiFormControlLabel-label.MuiTypography-root': {
      margin: theme.spacing(1.25, 0),
    },
    '&.MuiFormControlLabel-root.Mui-error .MuiCheckbox-root': {
      color: theme.palette.error.main,
    },
    // Error styles
    '& .MuiFormHelperText-root.Mui-error': {
      marginLeft: theme.spacing(0.5),
      display: 'flex',
      flexFlow: 'row nowrap',
      alignItems: 'center',
      '& .MuiSvgIcon-root[data-testid="ErrorIcon"]': {
        fontSize: 'inherit',
        transform: 'scale(1.25)',
        marginRight: theme.spacing(0.75),
        marginBottom: '1px',
      },
    },
  };
};

export default (component) => styled(component, {
  shouldForwardProp: (p) => p !== 'offColor',
})(styles);
